import ImportIcon from "@mui/icons-material/ExitToAppRounded";
import { Box, Button, Container } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import UploadDocumentsDialog from "../document-workflows/UploadDocumentsDialog";
import WorkflowGroupsGrid from "./WorkflowGroupsGrid";

const WorkflowGroupsPage = () => {
  usePageTitle("Workflow Runs");

  const { hasPermissions } = useClientContext();
  const canManageWorkflows = hasPermissions(["ManageWorkflows"]);

  const [workflowGroups, fetchError] = useFetch(adminApi.getWorkflowGroups);
  const [searchTerm, setSearchTerm] = useState("");
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);

  if (fetchError) {
    logError(fetchError, "[WorkflowGroupsPage] getWorkflowGroups");
    return <DataLoadingFailed />;
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim().toLocaleLowerCase());
  };

  const loading = workflowGroups === undefined;
  const groups = workflowGroups ?? [];

  const filteredGroups = searchTerm
    ? groups.filter(
        (group) =>
          group.category.toLocaleLowerCase().includes(searchTerm) ||
          group.name.toLocaleLowerCase().includes(searchTerm) ||
          (group.workflowChannelEmail ?? "").toLocaleLowerCase().includes(searchTerm)
      )
    : [...groups];

  return (
    <>
      <GeneralPageHeader title="Workflow Runs">
        {canManageWorkflows && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setMatchDialogOpen(true)}
            startIcon={<ImportIcon />}
          >
            Upload Documents
          </Button>
        )}
      </GeneralPageHeader>
      <Container maxWidth={false} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", mb: 2, pt: 2.5 }}>
          <RecordCounter records={filteredGroups.length} totalRecords={groups.length} hide={loading} />
          <SearchField debounceTimeMs={300} onSearch={handleSearch} />
        </Box>

        <WorkflowGroupsGrid rows={filteredGroups} loading={loading} />
      </Container>
      {matchDialogOpen && (
        <UploadDocumentsDialog
          onClose={() => setMatchDialogOpen(false)}
          mode={{ kind: "match", workflowGroups: groups }}
        />
      )}
    </>
  );
};

export default WorkflowGroupsPage;
